import { ApiError, apiRequest } from '../../api/client';
import type { CoinBalance } from '../../api/types';

export interface PendingCoins {
  pendingCoins: number;
}

export interface CoinTier {
  coins: number;
  cost_eur: number;
  [key: string]: unknown;
}

// S-03: GET /api/coins/costs liefert `image_tiers` seit dem
// Mitgliedschafts-Umbau (Commit 34491ecb) als FLACHES Objekt Stufe->Coins
// (server/coins.js: imageTierList = {standard, premium}) — NIE mehr ein Array
// von Objekten mit id/series_coins/available_for_series. Namen und
// Beschreibungen stehen im i18n-Katalog, NICHT in der Antwort: der Server
// nennt keine Modell- oder Anbieternamen (CLAUDE.md §17).
export type ImageTierId = 'standard' | 'premium';

/** Gemeinsamer Preis für Vorlesen und erzeugte Sprachnachrichten. */
export interface CharacterAudioCost {
  characters_per_coin: number;
  minimum: number;
  rounding: 'ceil';
}

export interface CoinCosts {
  chat_tiers?: CoinTier[];
  image_tiers?: Partial<Record<ImageTierId, number>>;
  /** Coin-Zuschlag fuer die Prompt-Erzeugung des Szenenbilds (heute 3). */
  scene_image_extra?: number;
  // Serverseitig `COIN_COSTS.campaigns` (server/coins.js) — seit Runde 2 auch
  // 'quick_adventure' und 'adventure_template' (Start einer Vorlage aus
  // „Entdecken", heute 0). Beide optional: alte Server liefern sie nicht.
  campaigns?: {
    campaign: number;
    table_round: number;
    quick_adventure?: number;
    adventure_template?: number;
  };
  tts?: CharacterAudioCost;
  audio_first?: CharacterAudioCost;
  session_recap?: CharacterAudioCost;
  character_voice?: CharacterAudioCost;
  stt?: { session_start: number; per_interval: number; interval_seconds: number; minimum: number; rounding: 'floor' };
  live_audio?: { coins_per_minute: number; interval_seconds: number; minimum_start: number };
  [key: string]: unknown;
}

// GET /api/coins/orders liefert die ROHZEILE aus `coin_orders`
// (server/repositories/coin-repository.js → getUserOrders: `SELECT *`).
// Die Spaltennamen sind deshalb `order_number` / `price_eur` /
// `payment_status` — die frueher hier deklarierten `amount_eur`/`status`
// existieren in der Antwort NICHT und blieben in der Tabelle leer.
// Beide Schreibweisen bleiben optional deklariert, damit ein spaeter
// normalisierender Server nicht bricht; gelesen wird defensiv (siehe
// index.tsx → orderAmountEur/orderStatus/orderNumber).
export interface CoinOrder {
  id: number;
  coins: number;
  order_number?: string;
  price_eur?: number;
  amount_eur?: number;
  payment_method?: string;
  payment_status?: string;
  status?: string;
  created_at?: string;
  invoice_id?: number | null;
  [key: string]: unknown;
}

export interface CoinTransaction {
  id: number;
  type?: string;
  expires_at?: string | null;
  remaining_coins?: number;
  amount: number;
  balance_after?: number;
  category?: string;
  description?: string;
  created_at?: string;
  live_session_id?: number | null;
  duration_seconds?: number | null;
  transaction_count?: number;
  [key: string]: unknown;
}

export interface CoinHistoryResponse {
  transactions: CoinTransaction[];
  total: number;
  limit: number;
  page: number;
}

export interface CoinSummaryCategory {
  spent?: number;
  earned?: number;
}

export interface CoinSummary {
  [category: string]: CoinSummaryCategory | number | undefined;
  total_spent?: number;
  total_purchased?: number;
}

export interface CoinNextExpiry {
  date: string;
  amount: number;
}

export interface NativePriceEntry {
  price: number;
  formatted: string;
  currency: string;
  [key: string]: unknown;
}

export interface NativePricesResponse {
  store: 'apple' | 'google';
  country: string;
  currency: string;
  prices: Record<string, NativePriceEntry>;
  updatedAt: string;
}

export interface RedeemResponse {
  success: boolean;
  coins?: number;
  balance?: number;
  error?: string;
}

export interface PromoValidateResponse {
  valid: boolean;
  code?: string;
  percent?: number;
  type?: 'discount' | 'bonus';
}

export interface FlatPrice {
  euroPrice: number;
  coinPrice: number;
}

export interface FlatPricesResponse {
  prices: Record<string, Record<string, FlatPrice>>;
  tiers: string[];
  durations: number[];
}

// Server-Rückgabe von GET /api/flats/active (siehe server/flats.js).
// Felder sind defensiv gelesen — Legacy-Bestellungen/Subscriptions können
// noch alte Tier-Namen (minimax/highspeed/…) liefern, die der Server
// bereits in FLAT_TIERS nachschlägt, der Client aber trotzdem robust
// verarbeiten muss.
export interface FlatActiveEntry {
  tier?: string;
  tierName?: string;
  tierNameEn?: string;
  tierEmoji?: string;
  includedModels?: string[];
  remainingHours?: number;
  expires_at?: string;
  subscriptionStatus?: string;
  subscriptionDuration?: string;
}

export interface FlatActiveResponse {
  active: boolean;
  isSubscription?: boolean;
  flat?: FlatActiveEntry;
}

export interface SubscriptionActive {
  tier?: string;
  interval?: string;
  status?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  // provider kommt aus der Roh-Zeile der flat_subscriptions-Tabelle
  // (siehe server/repositories/payment-repository.js). Erlaubt dem Client,
  // zwischen Stripe/PayPal (= in-App kündbar) und native IAP
  // (= nur im Store kündbar) zu unterscheiden.
  provider?: 'stripe' | 'paypal' | 'apple' | 'google' | (string & { readonly __unknownFutureProvider?: never });
}

// Ein Preis-Eintrag aus GET /api/subscriptions/prices — prices[tier][duration].
// Struktur exakt wie server/subscriptions.js → getSubscriptionPrices().
// `duration` ist 'weekly' | 'monthly' (NICHT Stunden wie bei den Flats).
export interface SubscriptionPriceEntry {
  euroPrice?: number;
  euroPriceCents?: number;
  coinPrice?: number;
  tierName?: string;
  tierNameEn?: string;
  tierEmoji?: string;
  duration?: string;
  durationLabel?: string;
  durationLabelEn?: string;
}

export interface SubscriptionPricesResponse {
  // prices enthaelt NUR die aktuell kaufbaren Stufen (PURCHASABLE_FLAT_TIERS).
  prices: Record<string, Record<string, SubscriptionPriceEntry | undefined> | undefined>;
  activeSub?: SubscriptionActive;
  // tiers ist die VOLLE Tier-Map (auch abgekuendigte Stufen) — Bestandsabos
  // loesen darueber ihren Anzeigenamen auf. Bewusst `unknown`-Werte: der Client
  // liest daraus nichts, was er nicht defensiv pruefen wuerde.
  tiers?: Record<string, unknown>;
  durations?: Record<string, unknown>;
}

export interface Invoice {
  id: number;
  order_id: number;
  status?: string;
  [key: string]: unknown;
}

// Phase 5: IAP-Produktliste kommt jetzt vom Server (GET /api/coins/apple-packages).
// Die dort gelieferten productIds (`eu.fluesterfeder.app.coins_*`) gelten
// identisch fuer Apple und Google. Das alte Platzhalter-Konstrukt
// (`COIN_PACKS`/`APPLE_COIN_PACKS`/`GOOGLE_COIN_PACKS`) wurde entfernt, da
// die Werte nirgendwo sonst verwendet wurden (geprueft via Grep).
export interface IapPack {
  id: string;
  appleId: string;
  coins: number;
  priceEur: number;
  name: string;
  /**
   * pending = neues Einstiegspaket, das erst nach Store-Freigabe kaufbar ist.
   * Wird nativ NUR angezeigt, wenn der Store (via native-prices) tatsächlich
   * einen Preis dafür liefert. Bestandspakete sind nicht pending → immer sichtbar.
   */
  pending?: boolean;
}

export interface IapPackagesResponse {
  packs: IapPack[];
  currency: string;
  note?: string;
}

export function getCoinIapPackages(): Promise<IapPackagesResponse> {
  return apiRequest<IapPackagesResponse>('/api/coins/apple-packages');
}

export function getPendingCoins(): Promise<PendingCoins> {
  return apiRequest<PendingCoins>('/api/coins/pending');
}

export function getCoinCosts(): Promise<CoinCosts> {
  return apiRequest<CoinCosts>('/api/coins/costs');
}

export function getCoinOrders(): Promise<CoinOrder[]> {
  return apiRequest<CoinOrder[]>('/api/coins/orders');
}

export function getCoinHistory(
  page = 1,
  limit = 20,
  category?: string,
): Promise<CoinHistoryResponse> {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (category && category !== 'all') params.set('category', category);
  return apiRequest<CoinHistoryResponse>(`/api/coins/history?${params.toString()}`);
}

export function getCoinSummary(): Promise<CoinSummary> {
  return apiRequest<CoinSummary>('/api/coins/summary');
}

export function getCoinNextExpiry(): Promise<CoinNextExpiry> {
  return apiRequest<CoinNextExpiry>('/api/coins/next-expiry');
}

// W7-A: Der Vorkasse-Weg (POST /api/coins/order mit paymentMethod
// 'prepayment') ist im Web bewusst AUFGEGEBEN (Julius' Entscheidung,
// W6C-entscheidungen.md §3). Die Server-Route bleibt fuer offene Alt-
// bestellungen unangetastet — es gibt in der SPA nur keinen Weg mehr dorthin.
// Aus demselben Grund fehlt hier ein Wrapper fuer
// POST /api/coins/order/:id/cancel: Stornieren war nur fuer Vorkasse-
// Bestellungen moeglich und ist ebenfalls aufgegeben.

// Anzeige-Rahmen der Spenden-Karte (Betraege NUR vom Server).
export interface DonationConfig {
  minEur: number;
  maxEur: number;
  defaultEur: number;
  presetsEur: number[];
  wishMaxLength: number;
  currency: string;
}

export function getDonationConfig(): Promise<DonationConfig> {
  return apiRequest<DonationConfig>('/api/coins/donation-config');
}

export function redeemGiftCode(code: string): Promise<RedeemResponse> {
  return apiRequest<RedeemResponse>('/api/gift-codes/redeem', { method: 'POST', body: { code } });
}

export function validatePromoCode(code: string): Promise<PromoValidateResponse> {
  return apiRequest<PromoValidateResponse>('/api/promo/validate', { method: 'POST', body: { code } });
}

export function createStripeCheckout(coins: number, source: 'pwa' | 'web'): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/stripe/create-checkout-session', {
    method: 'POST',
    body: { coins, source },
  });
}

export function verifyStripeSession(sessionId: string): Promise<{
  status: string;
  coins_added?: number;
  new_balance?: number;
  rewards?: { key: string; code?: string }[];
}> {
  return apiRequest(`/api/stripe/verify-session/${encodeURIComponent(sessionId)}`);
}

export function createDonation(amount: number, featureWish: string): Promise<{ url?: string }> {
  return apiRequest<{ url?: string }>('/api/stripe/create-donation', {
    method: 'POST',
    body: { amount, featureWish },
  });
}

// PayPal-Coinkauf: Der Server legt Order + Bestellung an und liefert die
// Approval-URL. Rechnungsdaten werden BEWUSST nicht abgefragt — der Capture
// uebernimmt Name/Adresse/E-Mail direkt aus der PayPal-Payer-Antwort
// (server/paypal.js → updatePayPalCoinOrderBilling), das Alt-Formular war
// reine Doppelerfassung.
export function createPaypalOrder(
  coins: number,
  source: 'pwa' | 'web',
): Promise<{ orderId?: string; approveUrl?: string }> {
  return apiRequest<{ orderId?: string; approveUrl?: string }>('/api/paypal/create-order', {
    method: 'POST',
    body: { coins, source },
  });
}

export function capturePaypalOrder(orderId: string): Promise<{
  success: boolean;
  coins_added?: number;
  new_balance?: number;
  rewards?: { key: string; code?: string }[];
}> {
  return apiRequest('/api/paypal/capture-order', { method: 'POST', body: { orderId } });
}

export function createDonationPaypal(
  amount: number,
  featureWish: string,
): Promise<{ orderId?: string; approveUrl?: string }> {
  return apiRequest<{ orderId?: string; approveUrl?: string }>('/api/paypal/create-donation', {
    method: 'POST',
    body: { amount, featureWish },
  });
}

export function captureDonationPaypal(orderId: string): Promise<{ success: boolean; error?: string }> {
  return apiRequest('/api/paypal/capture-donation', { method: 'POST', body: { orderId } });
}

export function getNativePrices(store: 'apple' | 'google', country: string): Promise<NativePricesResponse> {
  return apiRequest<NativePricesResponse>(
    `/api/public/native-prices?store=${encodeURIComponent(store)}&country=${encodeURIComponent(country)}`,
  );
}

export interface VerificationContext { attempt_id: string; recovery?: boolean }
export interface VerificationResponse {
  ok: boolean; status?: 'pending' | 'verified' | 'failed'; attempt_id?: string;
  coins?: number; balance?: number; tier?: string; error?: string; appleStatus?: number;
  error_code?: string; request_id?: string; retryable?: boolean;
}
export function verifyAppleReceipt(productId: string, receiptData: string, context?: VerificationContext): Promise<VerificationResponse> {
  return apiRequest('/api/apple/verify-receipt', { method: 'POST', body: { productId, receiptData, ...context } });
}
export function refreshAppleSubs(receiptData: string, context?: VerificationContext): Promise<unknown> {
  return apiRequest('/api/apple/refresh-subs', { method: 'POST', body: { receiptData, ...context } });
}
export function verifyGooglePurchase(
  productId: string, purchaseToken: string, productType: 'INAPP' | 'SUBS', basePlanId?: string,
  context?: VerificationContext,
): Promise<VerificationResponse> {
  return apiRequest('/api/google/verify-purchase', {
    method: 'POST', body: { productId, purchaseToken, productType, ...(basePlanId ? { basePlanId } : {}), ...context },
  });
}
export function getVerificationStatus(attemptId: string): Promise<VerificationResponse> {
  return apiRequest(`/api/iap/verification/${encodeURIComponent(attemptId)}`);
}
export function getGoogleReplaceCandidate(tokens: string[]): Promise<{ ok: boolean; index: number | null; contract_ref?: string }> {
  return apiRequest('/api/google/replace-candidate', { method: 'POST', body: { tokens: tokens.slice(0, 10) } });
}
export function refreshGoogleSubs(purchaseToken: string, context?: VerificationContext): Promise<unknown> {
  return apiRequest('/api/google/refresh-subs', { method: 'POST', body: { purchaseToken, ...context } });
}

export function getFlatPrices(): Promise<FlatPricesResponse> {
  return apiRequest<FlatPricesResponse>('/api/flats/prices');
}

export function getActiveFlat(): Promise<FlatActiveResponse> {
  return apiRequest<FlatActiveResponse>('/api/flats/active');
}

export function flatStripeCheckout(tier: string, durationHours: number): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/flats/stripe-checkout', {
    method: 'POST',
    body: { tier, durationHours },
  });
}

export function flatPaypalCreate(
  tier: string,
  durationHours: number,
): Promise<{ approveUrl?: string; orderId?: string }> {
  return apiRequest<{ approveUrl?: string; orderId?: string }>('/api/flats/paypal-create', {
    method: 'POST',
    body: { tier, durationHours },
  });
}

// Rueckweg von PayPal fuer den Flat-EINZELKAUF: PayPal haengt die Order-ID als
// `token` an die Rueckkehr-URL (server/flats.js → paymentReturnUrl mit
// flat=paypal_success). Ohne diesen Aufruf bliebe die Flat 'pending'.
export function flatPaypalCapture(orderId: string): Promise<{
  success?: boolean;
  already_active?: boolean;
  error?: string;
}> {
  return apiRequest('/api/flats/paypal-capture', { method: 'POST', body: { orderId } });
}

export function flatPurchaseCoins(
  tier: string,
  durationHours: number,
): Promise<{ success: boolean; coinBalance?: number; error?: string }> {
  return apiRequest('/api/flats/purchase-coins', {
    method: 'POST',
    body: { tier, durationHours },
  });
}

// Kein Wrapper fuer POST /api/flats/vorkasse — Vorkasse ist im Web aufgegeben
// (W6C-entscheidungen.md §3). Die Server-Route bleibt fuer offene Alt-
// bestellungen bestehen.

export function flatActivateTester(): Promise<{ success?: boolean; error?: string }> {
  return apiRequest('/api/flats/activate-tester', { method: 'POST' });
}

export function getSubscriptionPrices(): Promise<SubscriptionPricesResponse> {
  return apiRequest<SubscriptionPricesResponse>('/api/subscriptions/prices');
}

// ─── Web-Abos neu abschliessen (Stripe + PayPal) ─────────────────────────────
// `duration` ist hier 'weekly' | 'monthly' — server/subscriptions.js schlaegt
// daraus die Laufzeit UND (bei PayPal) die kanonische Plan-ID nach
// (app_meta-Cache, Boot-Guard). Deshalb NIE Stunden schicken.
export function subscriptionStripeCheckout(
  tier: string,
  duration: 'weekly' | 'monthly',
): Promise<{ url?: string; sessionId?: string }> {
  return apiRequest<{ url?: string; sessionId?: string }>('/api/subscriptions/stripe-checkout', {
    method: 'POST',
    body: { tier, duration },
  });
}

export function subscriptionPaypalCreate(
  tier: string,
  duration: 'weekly' | 'monthly',
): Promise<{ approveUrl?: string; subscriptionId?: string }> {
  return apiRequest<{ approveUrl?: string; subscriptionId?: string }>('/api/subscriptions/paypal-create', {
    method: 'POST',
    body: { tier, duration },
  });
}

export function cancelSubscription(): Promise<{
  success?: boolean;
  message?: string;
  endsAt?: string;
}> {
  return apiRequest('/api/subscriptions/cancel', { method: 'POST' });
}

export function getInvoices(): Promise<Invoice[]> {
  return apiRequest<Invoice[]>('/api/invoices');
}

export function getInvoiceDownloadUrl(id: number): string {
  return `/api/invoices/${encodeURIComponent(String(id))}/download`;
}

export { getCoinBalance } from '../../api/endpoints';
export type { CoinBalance };

export function readApiError(error: unknown, fallbackKey = 'coins.connection_error'): string {
  if (error instanceof ApiError) {
    if (error.data && typeof error.data === 'object' && 'error' in error.data) {
      const message = (error.data as { error: unknown }).error;
      if (typeof message === 'string' && message.trim()) return message;
    }
    return fallbackKey;
  }
  return fallbackKey;
}
